(() => {
  if (customElements.get('croma-popup')) return;
  class CromaPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog?.showModal || this.events) return;
      this.events = new AbortController();
      const options = {signal: this.events.signal};
      this.key = `croma:popup:${this.dataset.popupId}:${this.dataset.campaign}`;
      this.editor = this.dataset.editor === 'true';
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-popup-close], [data-popup-cta]')) this.dialog.close();
        if (event.target === this.dialog) {
          const rect = this.dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
        }
      }, options);
      this.dialog.addEventListener('close', () => {
        this.restorePage();
        this.dispatchEvent(new CustomEvent('croma:popup:closed', {bubbles: true}));
      }, options);
      this.addEventListener('submit', (event) => {
        if (this.editor) { event.preventDefault(); return; }
        try {
          sessionStorage.setItem('croma:popup:submission', JSON.stringify({id: this.dataset.popupId, at: Date.now()}));
        } catch { /* The form anchor also identifies the response. */ }
      }, options);
      if (this.editor) {
        document.addEventListener('shopify:section:select', (event) => {
          if (event.detail.sectionId === this.dataset.sectionId && this.isFirst()) this.preview();
        }, options);
        document.addEventListener('shopify:section:deselect', (event) => {
          if (event.detail.sectionId === this.dataset.sectionId) this.dialog.close();
        }, options);
        document.addEventListener('shopify:section:load', (event) => {
          if (event.detail.sectionId === this.dataset.sectionId && this.isFirst()) this.preview();
        }, options);
        document.addEventListener('shopify:block:select', (event) => {
          if (event.detail.blockId === this.dataset.blockId) this.preview();
        }, options);
        document.addEventListener('shopify:block:deselect', (event) => {
          if (event.detail.blockId === this.dataset.blockId) this.dialog.close();
        }, options);
      }
    }

    isFirst() { return this.parentElement.querySelector('croma-popup') === this; }

    preview() {
      this.parentElement.querySelectorAll('croma-popup').forEach((popup) => {
        if (popup !== this) {
          popup.dialog?.close();
          popup.restorePage();
        }
      });
      this.open();
    }

    wasSeen() {
      try {
        const lastSeen = Number(localStorage.getItem(this.key) || 0);
        const days = {daily: 1, weekly: 7, monthly: 30}[this.dataset.frequency] || 1;
        return lastSeen > 0 && Date.now() - lastSeen < days * 86400000;
      } catch { /* Storage blocked: allow the current page to work. */ }
      return false;
    }

    remember() {
      if (this.editor) return;
      try {
        localStorage.setItem(this.key, String(Date.now()));
      } catch { /* No persistent storage available. */ }
    }

    open() {
      if (!this.isConnected || this.dialog.open) return;
      // Never interrupt an open menu, cart drawer, or another dialog.
      if (document.hidden || document.querySelector('dialog[open], .croma-mobile-menu.is-open, .croma-cart-drawer.is-open')) {
        if (!this.editor) this.timer = setTimeout(() => this.open(), 500);
        return;
      }
      this.previousFocus = document.activeElement;
      this.previousOverflow = document.documentElement.style.overflow;
      this.dialog.showModal();
      this.locked = true;
      document.documentElement.style.overflow = 'hidden';
      this.lenis = window.Croma?.lenis;
      this.resumeLenis = Boolean(this.lenis && !this.lenis.isStopped);
      if (this.resumeLenis) this.lenis.stop();
      this.remember();
      this.querySelector('[data-popup-result]:not([hidden])')?.focus({preventScroll: true});
    }

    restorePage() {
      if (!this.locked) return;
      this.locked = false;
      document.documentElement.style.overflow = this.previousOverflow;
      if (this.resumeLenis) this.lenis.start();
      if (this.previousFocus?.isConnected) this.previousFocus.focus({preventScroll: true});
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.dialog?.close();
      this.restorePage();
      this.events?.abort();
      this.events = null;
    }
  }
  customElements.define('croma-popup', CromaPopup);

  class CromaPopupGroup extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.processed = new Set();
      this.addEventListener('croma:popup:closed', (event) => {
        if (event.target !== this.current) return;
        this.current = null;
        this.scheduleNext();
      }, {signal: this.events.signal});
      queueMicrotask(() => {
        if (!this.isConnected || this.dataset.editor === 'true') return;
        this.popups = Array.from(this.querySelectorAll('croma-popup'));
        const responsePopup = this.responsePopup();
        if (responsePopup) {
          this.current = responsePopup;
          this.processed.add(responsePopup);
          responsePopup.open();
        } else this.scheduleNext();
      });
    }

    responsePopup() {
      let pending;
      try { pending = JSON.parse(sessionStorage.getItem('croma:popup:submission')); } catch { /* Optional. */ }
      const popup = this.popups.find((item) => {
        const form = item.querySelector('form');
        const anchored = form && location.hash === `#${form.id}`;
        const recent = pending?.id === item.dataset.popupId && Date.now() - pending.at < 600000;
        return (anchored || recent) && item.querySelector('[data-popup-result]');
      });
      if (!popup) return null;
      const result = popup.querySelector('[data-popup-result]');
      result.hidden = false;
      if (result.dataset.popupResult === 'success') popup.querySelector('[data-popup-fields]').hidden = true;
      else {
        popup.querySelectorAll('input[type="email"]').forEach((input) => {
          input.setAttribute('aria-invalid', 'true');
          input.setAttribute('aria-describedby', result.id);
        });
      }
      try { sessionStorage.removeItem('croma:popup:submission'); } catch { /* Optional. */ }
      return popup;
    }

    scheduleNext() {
      if (!this.isConnected || this.dataset.editor === 'true' || this.current) return;
      const next = this.popups?.find((popup) => !this.processed.has(popup) && popup.dataset.enabled === 'true' && !popup.wasSeen());
      if (!next) return;
      this.processed.add(next);
      this.current = next;
      this.timer = setTimeout(() => next.open(), Number(next.dataset.delay || 0) * 1000);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.events?.abort();
      this.events = null;
      this.current = null;
    }
  }
  customElements.define('croma-popup-group', CromaPopupGroup);
})();
